import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Send,
  Loader2,
  Lock,
  Building2,
  User,
  Car,
  CheckCircle2,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

/**
 * OnboardReview — final step of the onboarding flow. Lists every tenant
 * + their vehicles, shows the invoice estimate, and exposes the only
 * place a manager can submit-for-invoice. From the Billing page this
 * action is intentionally NOT available.
 */
export default function OnboardReview() {
  const { activeProperty } = useAuth();
  const nav = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [details, setDetails] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!activeProperty) return;
    try {
      const [t, v, d, inv] = await Promise.all([
        api.get(`/tenants?property_id=${activeProperty.id}`),
        api.get(`/vehicles?property_id=${activeProperty.id}`),
        api.get(`/billing/details?property_id=${activeProperty.id}`),
        api.get(`/billing/invoices?property_id=${activeProperty.id}`),
      ]);
      setTenants(t.data || []);
      setVehicles(v.data || []);
      setDetails(d.data);
      setInvoices(inv.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [activeProperty]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/billing/finalize-onboarding", {
        property_id: activeProperty.id,
      });
      toast.success(
        `Service started — invoice ${data.invoice_number} ready for $${data.total.toFixed(2)}`
      );
      setConfirmOpen(false);
      nav("/dashboard/billing", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (!activeProperty || !details) {
    return <div className="text-sm text-[#71717A]">Loading…</div>;
  }

  const est = details.invoice_estimate || {
    active_cars: 0,
    billable_units: 0,
    monthly_subtotal: 0,
    monthly_full_subtotal: 0,
    setup_fee: details.setup_fee_paid ? 0 : 99,
    total: 99,
    tier_label: null,
    quote_required: false,
    free_month_applied: false,
  };
  const estCars = est.active_cars ?? est.billable_units ?? 0;
  const openInvoice = invoices.find(
    (i) => i.status === "pending" || i.status === "sent" || i.status === "overdue"
  );
  const tenantsByUnit = {};
  tenants.forEach((t) => {
    const key = `${t.building || ""}|${t.unit_number || ""}`;
    tenantsByUnit[key] = tenantsByUnit[key] || { ...t, vehicles: [] };
  });
  const vehMap = {};
  vehicles.forEach((v) => (vehMap[v.tenant_id] = (vehMap[v.tenant_id] || []).concat(v)));
  const totalVehicles = vehicles.length;
  const canSubmit = !openInvoice && (totalVehicles > 0 || est.setup_fee > 0);

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <Button variant="ghost" onClick={() => nav("/dashboard/onboard")} data-testid="review-back">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to enrollment
      </Button>

      <div>
        <div className="mono-tiny text-[#71717A]">Enrollment · Final step</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">
          Review &amp; start your service
        </h1>
        <p className="text-[#71717A] text-sm mt-2 max-w-2xl">
          One last look — confirm the details below, then click <b>Start
          Your Service</b> and we'll automatically generate your first PDF
          invoice. Your QR codes stay <b>Pending</b> until ScanMint marks
          the invoice paid (or your Stripe payment succeeds).
        </p>
      </div>

      {/* Pricing summary */}
      <section
        className="bg-white border border-[#E4E4E7] rounded-xl p-6"
        data-testid="review-pricing-card"
      >
        <div className="mono-tiny text-[#71717A] mb-3">Pricing</div>
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div className="border border-[#FACC15]/40 bg-[#FFFBEA] rounded-md p-4">
            <div className="mono-tiny text-[#92400E]">One-time</div>
            <div className="font-display text-xl font-bold mt-1">$99 setup fee</div>
            <div className="text-xs text-[#3F3F46] mt-1">
              Inkless mini printer + QR sticker supply + welcome packet.
            </div>
          </div>
          <div className="border border-[#0F8F4F]/30 bg-[#0F8F4F]/5 rounded-md p-4">
            <div className="mono-tiny text-[#0F8F4F]">Monthly</div>
            <div className="font-display text-xl font-bold mt-1">$1.00 / car</div>
            <div className="text-xs text-[#3F3F46] mt-1">
              Every active car billed on the 1st of every month. Discounts at 200/500 cars.
            </div>
          </div>
          <div className="border border-[#0EA5E9]/30 bg-[#0EA5E9]/5 rounded-md p-4">
            <div className="mono-tiny text-[#0369A1]">Welcome bonus</div>
            <div className="font-display text-xl font-bold mt-1">First month FREE</div>
            <div className="text-xs text-[#3F3F46] mt-1">
              The 30 days after the $99 setup is paid are free of monthly charges.
            </div>
          </div>
        </div>
      </section>

      {/* Property snapshot */}
      <section className="bg-white border border-[#E4E4E7] rounded-xl p-6" data-testid="review-property-card">
        <div className="flex items-center gap-3 pb-4 border-b border-[#E4E4E7]">
          <Building2 className="w-5 h-5 text-[#0F8F4F]" strokeWidth={1.5} />
          <div>
            <div className="mono-tiny text-[#71717A]">Property</div>
            <div className="font-display text-lg font-bold">
              {activeProperty.property_name || activeProperty.corporation_name}
            </div>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4 pt-4 text-sm">
          <Field label="Bill to">
            <div>{activeProperty.legal_company_name || activeProperty.corporation_name}</div>
            <div className="mono-tiny text-[#71717A]">
              {activeProperty.billing_contact_email || activeProperty.manager_email}
            </div>
          </Field>
          <Field label="Property address">
            <div>{activeProperty.address}</div>
            <div className="mono-tiny text-[#71717A]">
              {activeProperty.city}, {activeProperty.state} {activeProperty.zip}
            </div>
          </Field>
        </div>
      </section>

      {/* Tenants + vehicles */}
      <section className="bg-white border border-[#E4E4E7] rounded-xl overflow-hidden" data-testid="review-tenants-card">
        <div className="flex items-center justify-between gap-3 p-5 border-b border-[#E4E4E7]">
          <div className="flex items-center gap-3">
            <User className="w-5 h-5 text-[#0F8F4F]" strokeWidth={1.5} />
            <div>
              <div className="mono-tiny text-[#71717A]">Tenants &amp; vehicles</div>
              <div className="font-display text-lg font-bold">
                {tenants.length} tenant{tenants.length === 1 ? "" : "s"} · {totalVehicles} vehicle{totalVehicles === 1 ? "" : "s"}
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => nav("/dashboard/onboard")}
            data-testid="review-add-more"
          >
            Add more
          </Button>
        </div>
        {tenants.length === 0 ? (
          <div className="p-6 text-sm text-[#71717A]" data-testid="review-empty">
            No tenants added yet — add at least one tenant + one vehicle before submitting.
          </div>
        ) : (
          <div className="divide-y divide-[#E4E4E7]">
            {tenants.map((t) => (
              <div key={t.id} className="p-5" data-testid={`review-tenant-${t.id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-medium">{t.full_name}</div>
                    <div className="mono-tiny text-[#71717A] mt-0.5">
                      {t.building ? `Bldg ${t.building} · ` : ""}Unit {t.unit_number}
                    </div>
                  </div>
                  <div className="text-xs text-[#71717A]">
                    {(vehMap[t.id] || []).length} vehicle
                    {(vehMap[t.id] || []).length === 1 ? "" : "s"}
                  </div>
                </div>
                <div className="mt-2 grid sm:grid-cols-2 gap-2">
                  {(vehMap[t.id] || []).map((v) => (
                    <div
                      key={v.id}
                      className="flex items-center gap-2 text-sm py-1.5 px-2 bg-[#FAFAFA] rounded-md border border-[#E4E4E7]"
                      data-testid={`review-vehicle-${v.id}`}
                    >
                      <Car className="w-3.5 h-3.5 text-[#0EA5E9]" strokeWidth={1.5} />
                      <span className="truncate">
                        {v.year} {v.make} {v.model} ·{" "}
                        <span className="font-mono">{v.license_plate}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Estimate + Submit */}
      <section
        className="rounded-xl p-6 md:p-8 border border-[#0F8F4F]/30 bg-[#0F8F4F]/5"
        data-testid="review-submit-card"
      >
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <div className="mono-tiny text-[#0F8F4F]">Estimated invoice total</div>
            <div className="font-display text-3xl md:text-4xl font-bold mt-1" data-testid="review-total">
              ${est.total.toFixed(2)}
            </div>
            <div className="text-sm text-[#3F3F46] mt-2">
              {est.setup_fee > 0 ? "$99 setup + " : ""}
              {est.quote_required ? (
                <span className="text-[#92400E]">Enterprise quote ({estCars} cars)</span>
              ) : est.free_month_applied ? (
                <>
                  {estCars} car{estCars === 1 ? "" : "s"} × $1.00 ={" "}
                  <s className="text-[#71717A]">${(est.monthly_full_subtotal ?? 0).toFixed(2)}</s>{" "}
                  <b className="text-[#0F8F4F]">FREE first month</b>
                </>
              ) : (
                <>
                  {estCars} car{estCars === 1 ? "" : "s"} × $1.00 = ${est.monthly_subtotal.toFixed(2)}/mo
                </>
              )}
            </div>
            {openInvoice && (
              <div className="text-xs text-[#92400E] mt-3 flex items-center gap-2" data-testid="review-blocked">
                <Lock className="w-3.5 h-3.5" /> Invoice {openInvoice.invoice_number} is already open. View it on the Billing page.
              </div>
            )}
          </div>
          <Button
            onClick={() => setConfirmOpen(true)}
            disabled={!canSubmit || submitting}
            className="h-12 px-8 bg-[#0F8F4F] hover:bg-[#0A6B3B] shadow-sm"
            data-testid="review-submit-btn"
          >
            <Send className="w-4 h-4 mr-2" /> {openInvoice ? "View invoice" : "Start Your Service"}
          </Button>
        </div>
      </section>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="review-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Start your service?</DialogTitle>
            <DialogDescription>
              We'll generate your first PDF invoice for{" "}
              <b>${est.total.toFixed(2)}</b>, due on the 1st of next month.
              QR codes stay pending until the invoice is marked paid (by
              ScanMint Admin or via Stripe checkout from the Billing page).
            </DialogDescription>
          </DialogHeader>
          <ul className="text-sm space-y-2 text-[#3F3F46]">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#0F8F4F] mt-0.5 shrink-0" />{" "}
              {est.quote_required ? (
                <>Enterprise quote — {estCars} active cars · ScanMint will follow up</>
              ) : est.free_month_applied ? (
                <>{estCars} car{estCars === 1 ? "" : "s"} · <b className="text-[#0F8F4F]">first month FREE</b>, then $1.00 each</>
              ) : (
                <>{estCars} car{estCars === 1 ? "" : "s"} × $1.00 monthly billing</>
              )}
            </li>
            {est.setup_fee > 0 && (
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#0F8F4F] mt-0.5 shrink-0" /> $99 one-time setup fee (printer + sticker supply + welcome packet)
              </li>
            )}
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#0F8F4F] mt-0.5 shrink-0" /> All {totalVehicles} vehicle{totalVehicles === 1 ? "" : "s"} move to Pending Payment
            </li>
          </ul>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} disabled={submitting} data-testid="review-confirm-cancel">
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={submitting}
              className="bg-[#0F8F4F] hover:bg-[#0A6B3B]"
              data-testid="review-confirm-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Generate invoice
            </Button>          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="mono-tiny text-[#71717A]">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}
